import { useEffect, useCallback, useState, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { sanitizeHtml } from "@/lib/sanitize";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { useLandingEdit } from "./LandingEditContext";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Bold,
  Italic,
  Link as LinkIcon,
  Unlink,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface EditableTextProps {
  contentKey: string;
  className?: string;
  as?: "h1" | "h2" | "h3" | "p" | "span" | "div";
  multiline?: boolean;
  placeholder?: string;
}

export function EditableText({
  contentKey,
  className,
  as: Component = "span",
  multiline = false,
  placeholder = "Click to edit...",
}: EditableTextProps) {
  const { isEditMode, getContent, updateLocalContent } = useLandingEdit();
  const currentValue = getContent(contentKey);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: multiline ? {} : false,
        orderedList: multiline ? {} : false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        hardBreak: multiline ? {} : false,
        link: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-primary underline",
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: currentValue || "",
    editable: isEditMode,
    onUpdate: ({ editor }) => {
      if (isEditMode) {
        updateLocalContent(contentKey, editor.getHTML());
      }
    },
    onSelectionUpdate: ({ editor }) => {
      const { from, to } = editor.state.selection;
      setShowToolbar(from !== to);
    },
    onFocus: () => {
      if (isEditMode) setShowToolbar(true);
    },
    onBlur: () => {
      setTimeout(() => setShowToolbar(false), 200);
    },
    editorProps: {
      handleKeyDown: (view, event) => {
        if (!multiline && event.key === "Enter") {
          event.preventDefault();
          return true;
        }
        return false;
      },
    },
  });

  useEffect(() => {
    if (editor && editor.isEditable !== isEditMode) {
      editor.setEditable(isEditMode);
    }
  }, [editor, isEditMode]);

  useEffect(() => {
    if (editor && !isEditMode) {
      const currentHtml = editor.getHTML();
      if (currentHtml !== currentValue) {
        editor.commands.setContent(currentValue || "");
      }
    }
  }, [editor, currentValue, isEditMode]);

  const setLink = useCallback(() => {
    if (!editor || !linkUrl) return;
    
    if (linkUrl === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: linkUrl }).run();
    }
    setLinkUrl("");
    setLinkPopoverOpen(false);
  }, [editor, linkUrl]);

  const removeLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().unsetLink().run();
  }, [editor]);

  if (!isEditMode) {
    return (
      <Component 
        className={className} 
        data-testid={`editable-${contentKey}`}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentValue || placeholder) }}
      />
    );
  }

  if (!editor) {
    return (
      <Component 
        className={className} 
        data-testid={`editable-${contentKey}`}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentValue || placeholder) }}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative inline-block w-full",
        isEditMode && [
          "ring-2 ring-primary/50 ring-offset-2 ring-offset-background",
          "rounded-sm",
        ]
      )}
      data-testid={`editable-${contentKey}`}
    >
      {isEditMode && showToolbar && (
        <div className="absolute -top-12 left-0 z-50 flex items-center gap-1 p-1 bg-popover border rounded-md shadow-md">
          <Button
            size="icon"
            variant={editor.isActive("bold") ? "default" : "ghost"}
            onClick={() => editor.chain().focus().toggleBold().run()}
            className="h-8 w-8"
            type="button"
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant={editor.isActive("italic") ? "default" : "ghost"}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className="h-8 w-8"
            type="button"
          >
            <Italic className="h-4 w-4" />
          </Button>
          <Popover open={linkPopoverOpen} onOpenChange={setLinkPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                size="icon"
                variant={editor.isActive("link") ? "default" : "ghost"}
                className="h-8 w-8"
                type="button"
              >
                <LinkIcon className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-2" align="start">
              <div className="flex gap-2">
                <Input
                  placeholder="Enter URL..."
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      setLink();
                    }
                  }}
                  className="flex-1"
                />
                <Button size="sm" onClick={setLink} type="button">
                  Add
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          {editor.isActive("link") && (
            <Button
              size="icon"
              variant="ghost"
              onClick={removeLink}
              className="h-8 w-8"
              type="button"
            >
              <Unlink className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}
      <div className={className}>
        <EditorContent
          editor={editor}
          className={cn(
            "[&_.ProseMirror]:outline-none",
            "[&_.ProseMirror]:min-h-[1.5em]",
            "[&_.ProseMirror]:inline",
            "[&_.ProseMirror_p]:inline",
            isEditMode && "[&_.ProseMirror]:px-1"
          )}
        />
      </div>
    </div>
  );
}
